/**
 * The high-score table's data + composition layer — shared by every surface that mounts it: the
 * game-start screen (reveal-when-earned, mode caller-supplied), the dedicated `/leaderboard` +
 * `/bestenliste` page, and the menu-mounted overlay (leaderboard-dedicated-page-design; both new
 * surfaces deliberately end reveal-when-earned for themselves). Renders the server-capped top rows
 * (100 today, `BrowserActivity.TOP_ROWS_LIMIT`) + the visitor's row, scoped to the effective
 * gameMode. Reads the ranked projection from /api/leaderboard (projection-only since slice 3 —
 * stats are fed from the player's serde corpus at round end, not posted from here).
 *
 * Three sort tabs (Net / Rounds / Net/Runde) sit above the list. The active sort is stored on the
 * leaderboard container via {@code dataset.sortBy} so it survives re-renders without adding
 * module-level mutable state (the per-container {@code requestSeq} WeakMap below is the lone
 * exception — see static/js/CLAUDE.md § Module-level state exceptions).
 *
 * Since leaderboard-windowed-views, a second axis (7 Days / This Month / All-Time) sits alongside
 * the sort tabs, mirrored on {@code dataset.window}. Its first fetch per visit sends `window: null`
 * (cold start) and adopts the response's echoed `window` into the dataset before rendering — every
 * later click is an explicit pick, same shape as the sort tabs. `pairKey` grows the window
 * component so the stale-response guard covers both axes.
 *
 * Since leaderboard-expansion-window-records, that effective window (and `opts.month`) is folded
 * back into `settings` once it's settled, so the row-expansion fetch downstream asks for a panel
 * scoped to the same view the row heads show — a 7-day board opens a 7-day panel.
 *
 * Since leaderboard-dedicated-page-design, `renderLeaderboardInto` takes a third `opts` argument —
 * `onFirstRender` (inverts the game-start entrance dependency out of this now-shared module),
 * `modeStrip` (a third, caller-opt-in axis: Skat/Ramsch chips, mirroring the window axis), and
 * `heading` (default true; the two new surfaces render their own heading and pass false). See
 * `renderLeaderboardInto`'s own doc comment for the effective-settings derivation this grows.
 *
 * Since hall-of-fame, `opts.month`/`opts.monthLabel` turn the board into one closed month's record:
 * the window axis is fixed to MONTH (no cold start, no strip — 7 days and all-time are "now"
 * concepts a closed month has nothing to say about), the empty notice speaks the month, and the
 * viewer's tail row is earned by having played that month rather than by merely having a name.
 * Mode and sort stay live axes: a Ramsch-in-July board is a real page.
 *
 * Spec: specs/2026-05/2026-05-08-leaderboard-experiment-design.md +
 *       specs/2026-05/2026-05-11-bilanz-net-pr2-design.md +
 *       specs/2026-05/2026-05-16-leaderboard-stats-and-tabs-design.md +
 *       specs/2026-08/2026-08-01-leaderboard-windowed-views-design.md +
 *       specs/2026-08/2026-08-10-leaderboard-dedicated-page-design.md.
 */
import { t } from "../i18n.js";
import { fetchLeaderboard } from "./leaderboard-sync.js";
import { el } from "./leaderboard-format.js";
import { prefetchPanel } from "./leaderboard-expansion.js";
import {
  buildEmptyNotice,
  buildHeader,
  buildHeading,
  buildLoading,
  buildNudge,
  buildRow,
  buildSeparator,
  buildUnavailable,
} from "./leaderboard-render.js";

const SORT_TABS = ["NET", "ROUNDS", "AVG_NET_PER_ROUND"];
const SORT_TAB_LABEL_KEYS = {
  NET: "leaderboardTabNet",
  ROUNDS: "leaderboardTabRounds",
  AVG_NET_PER_ROUND: "leaderboardTabAvgNet",
};

const WINDOW_TABS = ["SEVEN_DAYS", "MONTH", "ALL_TIME"];
const WINDOW_TAB_LABEL_KEYS = {
  SEVEN_DAYS: "leaderboardWindow7d",
  MONTH: "leaderboardWindowMonth",
  ALL_TIME: "leaderboardWindowAll",
};

const MODE_TABS = ["SKAT", "RAMSCH"];
const MODE_TAB_LABEL_KEYS = { SKAT: "modeSkat", RAMSCH: "modeRamsch" };

// Per-container generation counter — a WeakMap, not a bare module-global, since two boards can now
// be live at once (a menu-opened overlay board over a still-mounted start-screen board): a shared
// counter would strand whichever board's in-flight fetch lands second on its loading placeholder.
// Infrastructure-state exception, same class as the bare counter it replaces (js/CLAUDE.md § Module-
// level state exceptions).
const requestSeq = new WeakMap();

/** Claims the next generation for `container` — the value a later `isStale` check compares itself
 *  against, so a slow, superseded fetch resolving out of order can tell it lost the race. */
function beginRequest(container) {
  const seq = (requestSeq.get(container) ?? 0) + 1;
  requestSeq.set(container, seq);
  return seq;
}

function isStale(container, seq) {
  return seq !== requestSeq.get(container);
}

/**
 * The effective settings this render pass acts on. Without `opts.modeStrip`, `settings` passes
 * through untouched — the start-screen call shape is unchanged, `effectiveMode === settings.
 * gameMode` always. With it, mode becomes a container-held axis like sortBy/window: seeded from
 * `settings.gameMode` into `container.dataset.gameMode` on first render (cold start — the caller's
 * initial pick), read back off the dataset on every render after (a mode-chip click mutates the
 * dataset directly, mirroring `onTabClick`/`onWindowClick`). The returned object — never the
 * caller's own `settings` — is what flows into `pairKey`, the fetch, and `buildView`/`buildRow`, so
 * the row-expansion stats-panel fetch requests the mode the rows actually show.
 */
function effectiveSettingsFor(container, settings, opts) {
  if (!opts.modeStrip) return settings;
  if (container.dataset.gameMode === undefined) {
    container.dataset.gameMode = settings.gameMode;
  }
  return { ...settings, gameMode: container.dataset.gameMode };
}

/**
 * Idempotent: replaces existing leaderboard content if called again. The container element is
 * owned by the caller (the game-start section, the page mount, or the overlay content div); this
 * module only fills it. Reads {@code container.dataset.sortBy} for the active tab, defaulting to
 * NET on first render.
 *
 * {@code container.dataset.window} mirrors {@code dataset.sortBy} for the window axis, but starts
 * absent (cold start) rather than defaulted client-side — the default window depends on server-side
 * counts this module doesn't have. A cold-start call sends {@code window: null} and, once the
 * response lands, adopts its echoed {@code window} into the dataset before building the view; every
 * later call (an explicit sort or window tab click) already has a dataset value to request and to
 * re-check the response against, the same staleness guard `sortBy` has always used. `opts.month`
 * seeds that dataset to MONTH before the cold-start read, so a month page never runs the adopt
 * branch — there is no server-resolved default to adopt when the client fixed the window.
 *
 * `opts.onFirstRender(container)` fires once per container instead of the old hardwired
 * `playLeaderboardReveal` import — game-start-active-runs.js passes that reveal in; the two new
 * surfaces pass nothing (a deliberate design call: the overlay has the shell's own open animation,
 * a page navigation paints as a load).
 */
export async function renderLeaderboardInto(container, settings, opts = {}) {
  // The render's own derivation, not the caller's settings — see effectiveSettingsFor above.
  // Reassigned rather than bound to a second name: every line below already reads `settings`,
  // so this keeps the pre-options-bag call shape verbatim wherever modeStrip is off.
  settings = effectiveSettingsFor(container, settings, opts);
  const sortBy = container.dataset.sortBy || "NET";
  container.dataset.sortBy = sortBy;
  const requestedWindow = requestedWindowFor(container, opts);
  const coldStart = requestedWindow === null;
  const selection = pairKey(settings, sortBy, requestedWindow);
  const month = opts.month ?? null;
  const seq = beginRequest(container);
  // Not on a cold start: which window this board will show is the server's to pick, so there is no
  // view to ask a panel for yet.
  if (!coldStart)
    prefetchViewerPanel(container, settings, requestedWindow, month);
  // why: only show the dot placeholder on a cold start. Replacing a populated
  // leaderboard with the placeholder collapses the container height ~80 px for
  // two frames before the new data swaps in — visible as a flicker on every
  // mode / tab click.
  if (container.children.length === 0)
    replaceContent(container, buildLoading());
  try {
    const mode = settings.gameMode;
    const data = await fetchLeaderboard(mode, sortBy, requestedWindow, month);
    if (isStale(container, seq)) return;
    if (shouldDiscardResponse(container, settings, coldStart, selection, data))
      return;
    // The effective window is only settled here — a cold start adopts the server's pick inside
    // shouldDiscardResponse above. Reassigned, not bound to a second name, the same convention
    // effectiveSettingsFor's own comment states; a re-entrant call's stale field is overwritten by
    // this fresh spread. From here it rides the existing chain (buildView -> buildRow -> toggleRow
    // -> expandDetail -> loadDetailFragment) with no signature change, so a row's expansion asks
    // for the panel of the window its head is showing (leaderboard-expansion-window-records § 5).
    settings = { ...settings, window: container.dataset.window, month };
    const view = buildView(data, settings, sortBy, container, opts);
    replaceContent(container, view);
    fireOnFirstRender(container, opts);
  } catch (e) {
    if (isStale(container, seq)) return;
    console.warn("[leaderboard] fetch failed:", e?.message ?? e);
    replaceContent(container, buildUnavailable());
  }
}

/** Cascade the rows in once per visit — not on every re-sort/mode/window click. The container is
 *  rebuilt on each game-start entry, so this dataset flag resets with it. */
function fireOnFirstRender(container, opts) {
  if (container.dataset.lbRevealed != null) return;
  container.dataset.lbRevealed = "1";
  opts.onFirstRender?.(container);
}

/** Starts the viewer row's panel fetch alongside the board's own, instead of after it: this render
 *  will re-open that row the moment the board lands, so the two round trips used to run in series
 *  (leaderboard-start-screen-click-cost § 2). `dataset.viewerEntry` is the row the last render
 *  actually built — absent before any render has landed, and on a board that shows no viewer row at
 *  all (a hall-of-fame month the viewer never played). */
function prefetchViewerPanel(container, settings, window, month) {
  const entryKey = container.dataset.viewerEntry;
  if (entryKey)
    prefetchPanel({ ...settings, window, month }, entryKey, container);
}

/**
 * The window this render asks the server for — null on a cold start, where the server applies its
 * own default rule and echoes what it picked. `opts.month` seeds the axis to MONTH first, so a
 * hall-of-fame board has no cold start at all: the client fixed the window, there is no
 * server-side default left to resolve.
 */
function requestedWindowFor(container, opts) {
  if (opts.month && container.dataset.window === undefined)
    container.dataset.window = "MONTH";
  return container.dataset.window ?? null;
}

function pairKey(settings, sortBy, window) {
  return `${settings.gameMode}.${sortBy}.${window}`;
}

/**
 * True when a just-landed response should be dropped rather than rendered. Cold start (no window
 * requested — the client didn't know the default) always adopts the echoed window and renders:
 * nothing can have raced it, since the window strip (and its click handlers) don't exist in the
 * DOM until this very response lands — the only possible interruption is a newer
 * `renderLeaderboardInto` call, already caught by the caller's per-container `requestSeq` check.
 * Every later call already has a `selection` to compare the live dataset against, same staleness
 * guard `sortBy` alone used before this axis existed.
 */
function shouldDiscardResponse(
  container,
  settings,
  coldStart,
  selection,
  data,
) {
  if (coldStart) {
    container.dataset.window = data.window;
    return false;
  }
  return (
    selection !==
    pairKey(settings, container.dataset.sortBy, container.dataset.window)
  );
}

function buildView(data, settings, sortBy, container, opts) {
  const wrap = el("div", "leaderboard");
  if (opts.heading !== false) wrap.appendChild(buildHeading());
  if (opts.modeStrip)
    wrap.appendChild(buildModeStrip(settings, container, opts));
  wrap.appendChild(buildTabStrip(settings, sortBy, container, opts));
  if (!opts.month)
    wrap.appendChild(buildWindowStrip(data, settings, container, opts));
  const hasTop = data.topRows.length > 0;
  const viewerTop = data.topRows.find((r) => r.isViewer) ?? null;
  const showViewerRow = viewerTailEarned(data, viewerTop != null, opts);
  // Which viewer row this board is showing, held on the container in the sortBy/window dataset
  // idiom — the entry the next chip click prefetches a panel for, before its own board response
  // lands (prefetchViewerPanel above). Whichever of the two sites builds the row is the one that
  // names it here; a render that builds none clears it.
  const viewerRow = viewerTop ?? (showViewerRow ? data.viewer : null);
  if (viewerRow == null) delete container.dataset.viewerEntry;
  else container.dataset.viewerEntry = viewerRow.entryKey;
  // The month notice states a fact about the month, so it renders on any empty board — including
  // beside an earned tail. The live surfaces still show one or the other, never both.
  if (!hasTop && (opts.month || !showViewerRow))
    wrap.appendChild(buildEmptyNotice(opts.monthLabel));
  if (!hasTop && !showViewerRow) return wrap;
  wrap.appendChild(buildHeader(sortBy));
  if (hasTop) wrap.appendChild(buildTopList(data, sortBy, settings, container));
  if (showViewerRow) appendViewerTail(wrap, data, sortBy, settings, container);
  return wrap;
}

/** The ranked top rows in rank order — the server-capped list the board leads with. */
function buildTopList(data, sortBy, settings, container) {
  const list = el("ol", "leaderboard-list");
  data.topRows.forEach((row, i) =>
    list.appendChild(buildRow(row, `#${i + 1}`, sortBy, settings, container)),
  );
  return list;
}

/**
 * Whether the viewer has earned their own tail row. On the live surfaces three different "this
 * visitor has engaged" signals all warrant pinning one: a set name, a nameless viewer's
 * provisional rank, and a named-but-pending one's (board-approval design). On a closed month it is
 * earned by participation in that month instead — the projection returns null for both ranks when
 * the viewer played no rounds there, and a "—" row for someone who sat July out would read as a
 * defect rather than as the fact it is (hall-of-fame design § 3).
 */
function viewerTailEarned(data, viewerInTop, opts) {
  if (viewerInTop) return false;
  if (opts.month)
    return (data.viewerRank ?? data.viewerProvisionalRank) != null;
  return data.viewer.name !== "" || data.viewerProvisionalRank != null;
}

/**
 * The viewer's own tail row: optional separator, the rank badge (real "#N", provisional "~#N", or
 * "—" when they haven't played this pair at all), and — provisional viewers only — the
 * name-yourself nudge, itself gated on having no local name (settings.playerName). Board-approval
 * design § Viewer row / provisional rank: a pending viewer who *has* typed a name still gets the
 * row + "~#N", but no nudge — there is nothing left for them to type. A real-ranked (board-visible)
 * viewer never gets the nudge either way, even if settings.playerName happens to be empty (e.g. a
 * cleared localStorage on an otherwise-still-visible entry) — the nudge's premise (typing a name
 * would help) doesn't hold once they're already on the board.
 */
function appendViewerTail(wrap, data, sortBy, settings, container) {
  const rank = data.viewerRank ?? data.viewerProvisionalRank;
  const provisional = data.viewerProvisionalRank != null;
  // Separator only when there are top rows AND the viewer row isn't immediately after them.
  const adjacent = rank != null && rank === data.topRows.length + 1;
  if (data.topRows.length > 0 && !adjacent) wrap.appendChild(buildSeparator());
  const tail = el("ol", "leaderboard-list leaderboard-list-tail");
  if (rank != null) tail.start = rank;
  const rankText =
    data.viewerRank != null ? `#${rank}` : provisional ? `~#${rank}` : "—";
  tail.appendChild(
    buildRow(data.viewer, rankText, sortBy, settings, container),
  );
  wrap.appendChild(tail);
  if (provisional && !settings.playerName) wrap.appendChild(buildNudge());
}

function buildTabStrip(settings, sortBy, container, opts) {
  const strip = el("div", "leaderboard-tabs");
  for (const tab of SORT_TABS) {
    const chip = el(
      "button",
      "leaderboard-tab" + (tab === sortBy ? " selected" : ""),
      t(SORT_TAB_LABEL_KEYS[tab]),
    );
    chip.type = "button";
    chip.dataset.sortBy = tab;
    chip.addEventListener("click", () =>
      onTabClick(container, settings, tab, opts, chip),
    );
    strip.appendChild(chip);
  }
  return strip;
}

function onTabClick(container, settings, nextSort, opts, chip) {
  if (container.dataset.sortBy === nextSort) return;
  container.dataset.sortBy = nextSort;
  selectChip(chip);
  void renderLeaderboardInto(container, settings, opts);
}

/** Moves `.selected` onto the just-clicked chip within its own strip, before the fetch goes out.
 *  Purely the interim state — the strip is rebuilt from the response and re-marks the same chip —
 *  but without it a chip click changes nothing on screen for a whole round trip, which on a slow
 *  connection reads as a dropped tap (leaderboard-start-screen-click-cost § 2). */
function selectChip(chip) {
  for (const sibling of chip.parentElement.children)
    sibling.classList.toggle("selected", sibling === chip);
}

/**
 * The window axis: visually subordinate to the metric tabs above it (decided call 4) — a second,
 * quieter strip, not a fourth metric tab. Every window stays clickable even at a zero count
 * (`windowCounts` only de-emphasizes it via `.is-empty`) — an empty window is still a legitimate
 * pick (e.g. checking "is anyone active this week yet").
 */
function buildWindowStrip(data, settings, container, opts) {
  const strip = el("div", "window-strip");
  for (const win of WINDOW_TABS) {
    const chip = el(
      "button",
      "window-tab" +
        (win === data.window ? " selected" : "") +
        (windowCount(data.windowCounts, win) === 0 ? " is-empty" : ""),
      t(WINDOW_TAB_LABEL_KEYS[win]),
    );
    chip.type = "button";
    chip.dataset.window = win;
    chip.addEventListener("click", () =>
      onWindowClick(container, settings, win, opts, chip),
    );
    strip.appendChild(chip);
  }
  return strip;
}

function windowCount(counts, win) {
  if (win === "SEVEN_DAYS") return counts.sevenDays;
  if (win === "MONTH") return counts.month;
  return counts.allTime;
}

function onWindowClick(container, settings, nextWindow, opts, chip) {
  if (container.dataset.window === nextWindow) return;
  container.dataset.window = nextWindow;
  selectChip(chip);
  void renderLeaderboardInto(container, settings, opts);
}

/**
 * The mode axis (opt-in via `opts.modeStrip`, § effectiveSettingsFor above): a third chip strip,
 * Skat/Ramsch, in the same pattern as the window strip above it — the page and overlay mounts are
 * the callers, letting a shared link/board show both modes rather than pinning one. Placement
 * above the sort tabs is accepted as rendered (Andre, 2026-08-10, one of the spec's open
 * questions): mode is the broadest axis, closer in spirit to the mode-card pick the start screen
 * makes before ever reaching a board at all.
 */
function buildModeStrip(settings, container, opts) {
  const strip = el("div", "leaderboard-mode-strip");
  for (const mode of MODE_TABS) {
    const chip = el(
      "button",
      "leaderboard-mode-tab" +
        (mode === container.dataset.gameMode ? " selected" : ""),
      t(MODE_TAB_LABEL_KEYS[mode]),
    );
    chip.type = "button";
    chip.dataset.gameMode = mode;
    chip.addEventListener("click", () =>
      onModeClick(container, settings, mode, opts, chip),
    );
    strip.appendChild(chip);
  }
  return strip;
}

function onModeClick(container, settings, nextMode, opts, chip) {
  if (container.dataset.gameMode === nextMode) return;
  container.dataset.gameMode = nextMode;
  selectChip(chip);
  void renderLeaderboardInto(container, settings, opts);
}

function replaceContent(container, child) {
  while (container.firstChild) container.removeChild(container.firstChild);
  container.appendChild(child);
}
